import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ClipboardList, ChevronRight, Lock, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

const inputCls = "w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/50 transition-colors";
const labelCls = "text-zinc-400 text-xs font-bold uppercase tracking-widest";

function Veld({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className={labelCls}>{label}</span>
      {children}
    </label>
  );
}

function Keuze({ opties, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opties.map(o => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${value === o ? "text-black border-transparent" : "bg-[#111] text-zinc-400 border-white/10 hover:border-white/25"}`}
          style={value === o ? { background: GOLD } : {}}>
          {o}
        </button>
      ))}
    </div>
  );
}

function MultiKeuze({ opties, values, onChange, testid }) {
  const toggle = (o) => onChange(values.includes(o) ? values.filter(v => v !== o) : [...values, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opties.map(o => (
        <button key={o} type="button" onClick={() => toggle(o)}
          className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all flex items-center gap-1.5 ${values.includes(o) ? "text-black border-transparent" : "bg-[#111] text-zinc-400 border-white/10 hover:border-white/25"}`}
          style={values.includes(o) ? { background: GOLD } : {}}>
          {values.includes(o) && <Check size={12} />}{o}
        </button>
      ))}
    </div>
  );
}

const SPIERGROEPEN = ["Borst", "Rug", "Schouders", "Biceps", "Triceps", "Benen", "Bilspieren", "Hamstrings", "Kuiten", "Core"];

export default function PTIntakePage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const korting = params.get("korting") || "";

  const [order, setOrder] = useState(null);
  const [stap, setStap] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [fout, setFout] = useState("");
  const [f, setF] = useState({
    doel: "", niveau: "", ervaring: "", tijd_per_training: "60",
    leeftijd: "", geslacht: "", lengte: "", gewicht: "",
    blessures: "", fysieke_beperkingen: "",
    oefeningen_niet_kunnen: "", oefeningen_niet_willen: "",
    locatie: "", apparatuur: [], materiaal_voorkeur: [],
    focus_spiergroepen: [], lagere_prioriteit: [],
    andere_sporten: "", opmerkingen: "",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  useEffect(() => {
    fetch(`${API}/api/orders/${orderId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setOrder(d))
      .catch(() => {});
  }, [orderId]);

  const stap1Ok = f.doel && f.niveau && f.leeftijd && f.geslacht;
  const stap2Ok = f.locatie && (f.apparatuur.length > 0 || f.locatie === "Sportschool");

  const handleAfrekenen = async () => {
    if (submitting) return;
    setSubmitting(true); setFout("");
    try {
      const intake = { ...f, apparatuur: f.apparatuur.join(", "), materiaal_voorkeur: f.materiaal_voorkeur.join(", "), focus_spiergroepen: f.focus_spiergroepen.join(", "), lagere_prioriteit: f.lagere_prioriteit.join(", ") };
      const r1 = await fetch(`${API}/api/pt/intake/${orderId}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(intake),
      });
      if (!r1.ok) throw new Error((await r1.json()).detail || "Opslaan mislukt");
      const r2 = await fetch(`${API}/api/pt/checkout/${orderId}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kortingscode: korting }),
      });
      const d2 = await r2.json();
      if (!r2.ok || !d2.checkout_url) throw new Error(d2.detail || "Betaling starten mislukt");
      window.location.href = d2.checkout_url;
    } catch (e) {
      setFout(e.message || "Er ging iets mis, probeer opnieuw.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] pb-20">
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <ClipboardList size={16} style={{ color: GOLD }} />
          <span className="text-zinc-400 text-sm">Intake — Persoonlijk Trainingsschema</span>
          <div className="ml-auto flex items-center gap-1.5 text-zinc-600 text-xs"><Lock size={11} /> Stap {stap} van 3</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-white font-black text-2xl mb-2">Vertel ons over jouw training</h1>
        <p className="text-zinc-500 text-sm mb-8">Op basis van deze intake stellen we jouw persoonlijke trainingsschema samen volgens de FitSmith-methode.</p>

        {stap === 1 && (
          <div className="space-y-6" data-testid="pt-intake-stap1">
            <Veld label="Wat is je trainingsdoel? *">
              <Keuze testid="intake-doel" opties={["Spiermassa", "Kracht", "Vetverlies", "Fit worden", "Conditie"]} value={f.doel} onChange={v => set("doel", v)} />
            </Veld>
            <Veld label="Wat is je niveau? *">
              <Keuze testid="intake-niveau" opties={["Beginner", "Gemiddeld", "Gevorderd"]} value={f.niveau} onChange={v => set("niveau", v)} />
            </Veld>
            <Veld label="Hoeveel trainingservaring heb je?">
              <Keuze opties={["< 6 maanden", "6-24 maanden", "2-5 jaar", "5+ jaar"]} value={f.ervaring} onChange={v => set("ervaring", v)} />
            </Veld>
            <Veld label="Beschikbare tijd per training">
              <Keuze opties={["30", "45", "60", "75", "90"]} value={f.tijd_per_training} onChange={v => set("tijd_per_training", v)} />
            </Veld>
            <div className="grid grid-cols-2 gap-4">
              <Veld label="Leeftijd *"><input className={inputCls} inputMode="numeric" value={f.leeftijd} onChange={e => set("leeftijd", e.target.value)} placeholder="bijv. 28" data-testid="intake-leeftijd" /></Veld>
              <Veld label="Geslacht *">
                <Keuze opties={["Man", "Vrouw", "Anders"]} value={f.geslacht} onChange={v => set("geslacht", v)} />
              </Veld>
              <Veld label="Lengte (cm)"><input className={inputCls} inputMode="numeric" value={f.lengte} onChange={e => set("lengte", e.target.value)} placeholder="bijv. 180" /></Veld>
              <Veld label="Gewicht (kg)"><input className={inputCls} inputMode="numeric" value={f.gewicht} onChange={e => set("gewicht", e.target.value)} placeholder="bijv. 78" /></Veld>
            </div>
            <button disabled={!stap1Ok} onClick={() => { setStap(2); window.scrollTo(0, 0); }} data-testid="intake-volgende-1"
              className="w-full py-4 rounded-xl font-bold text-sm text-black disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ background: GOLD }}>Volgende <ChevronRight size={15} /></button>
          </div>
        )}

        {stap === 2 && (
          <div className="space-y-6" data-testid="pt-intake-stap2">
            <Veld label="Waar train je? *">
              <Keuze testid="intake-locatie" opties={["Sportschool", "Thuis"]} value={f.locatie} onChange={v => set("locatie", v)} />
            </Veld>
            <Veld label="Welke apparatuur heb je beschikbaar?">
              <MultiKeuze testid="intake-apparatuur" opties={["Barbells", "Dumbbells", "Machines", "Kabels", "Kettlebells", "Weerstandsbanden", "Alleen lichaamsgewicht"]} values={f.apparatuur} onChange={v => set("apparatuur", v)} />
            </Veld>
            <Veld label="Voorkeur voor materiaal (optioneel)">
              <MultiKeuze opties={["Machines", "Dumbbells", "Barbells", "Kabels"]} values={f.materiaal_voorkeur} onChange={v => set("materiaal_voorkeur", v)} />
            </Veld>
            <Veld label="Blessures (beschrijf kort, of laat leeg)">
              <input className={inputCls} value={f.blessures} onChange={e => set("blessures", e.target.value)} placeholder="bijv. schouderklachten links" data-testid="intake-blessures" />
            </Veld>
            <Veld label="Fysieke beperkingen">
              <input className={inputCls} value={f.fysieke_beperkingen} onChange={e => set("fysieke_beperkingen", e.target.value)} placeholder="bijv. beperkte mobiliteit heup" />
            </Veld>
            <Veld label="Oefeningen die je niet kunt uitvoeren">
              <input className={inputCls} value={f.oefeningen_niet_kunnen} onChange={e => set("oefeningen_niet_kunnen", e.target.value)} placeholder="bijv. Deadlift" />
            </Veld>
            <Veld label="Oefeningen die je liever niet doet">
              <input className={inputCls} value={f.oefeningen_niet_willen} onChange={e => set("oefeningen_niet_willen", e.target.value)} placeholder="bijv. Bulgarian Split Squat" />
            </Veld>
            <div className="flex gap-3">
              <button onClick={() => setStap(1)} className="flex-1 py-4 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 text-zinc-300">Terug</button>
              <button disabled={!stap2Ok} onClick={() => { setStap(3); window.scrollTo(0, 0); }} data-testid="intake-volgende-2"
                className="flex-1 py-4 rounded-xl font-bold text-sm text-black disabled:opacity-40 flex items-center justify-center gap-2"
                style={{ background: GOLD }}>Volgende <ChevronRight size={15} /></button>
            </div>
          </div>
        )}

        {stap === 3 && (
          <div className="space-y-6" data-testid="pt-intake-stap3">
            <Veld label="Op welke spiergroepen wil je extra focus?">
              <MultiKeuze testid="intake-focus" opties={SPIERGROEPEN} values={f.focus_spiergroepen} onChange={v => set("focus_spiergroepen", v)} />
            </Veld>
            <Veld label="Spiergroepen met minder prioriteit">
              <MultiKeuze opties={SPIERGROEPEN} values={f.lagere_prioriteit} onChange={v => set("lagere_prioriteit", v)} />
            </Veld>
            <Veld label="Doe je andere sporten?">
              <input className={inputCls} value={f.andere_sporten} onChange={e => set("andere_sporten", e.target.value)} placeholder="bijv. voetbal 2x per week" />
            </Veld>
            <Veld label="Aanvullende opmerkingen">
              <textarea className={inputCls} rows={3} value={f.opmerkingen} onChange={e => set("opmerkingen", e.target.value)} placeholder="Alles wat we verder moeten weten..." />
            </Veld>

            <div className="rounded-2xl border border-white/8 p-5" style={{ background: "#111" }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-bold text-sm">Persoonlijk Trainingsschema</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{order?.pt_dagen ? `${order.pt_dagen} trainingsdagen per week · ` : ""}Samengesteld op basis van jouw intake</p>
                </div>
                <p className="font-black text-xl" style={{ color: GOLD }} data-testid="intake-prijs">
                  €{Number(order?.price ?? 0).toFixed(2).replace(".", ",")}
                </p>
              </div>
            </div>

            {fout && <p className="text-red-400 text-sm text-center">{fout}</p>}
            <div className="flex gap-3">
              <button onClick={() => setStap(2)} className="flex-1 py-4 rounded-xl font-semibold text-sm bg-white/5 border border-white/10 text-zinc-300">Terug</button>
              <button onClick={handleAfrekenen} disabled={submitting} data-testid="intake-afrekenen-btn"
                className="flex-1 py-4 rounded-xl font-bold text-sm text-black disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ background: GOLD }}>
                <Lock size={13} /> {submitting ? "Even geduld..." : "Veilig afrekenen"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
